import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Package } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SEOHead from '../components/seo/SEOHead';
import TiltCard from '../components/effects/TiltCard';
import productsData from '../data/completeProductsData';
import { trackPageView } from '../utils/analytics';

const CategoryPage = () => {
  const { categorySlug } = useParams();
  
  useEffect(() => {
    trackPageView(`Category: ${categorySlug}`);
  }, [categorySlug]);

  // Find category data by slug
  const getCategoryData = () => {
    const categories = [
      productsData.laryngoStroboscopyProcessors,
      productsData.surgeryProcessors,
      productsData.officeMobileProcessors,
      productsData.cameraHeads,
      productsData.flexibleEndoscopes,
      productsData.fibreOpticEndoscopes,
      productsData.compactCameras,
      productsData.rigidEndoscopes,
      productsData.illuminationSystems,
      productsData.documentationSystems,
      productsData.medicalMonitors,
      productsData.softwareSystems
    ];
    return categories.find(cat => cat.slug === categorySlug);
  };

  const category = getCategoryData();

  if (!category) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-black pt-32 px-6">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-4xl font-bold text-white mb-4">Category Not Found</h1>
            <Link to="/products" className="btn-primary">
              Back to Products
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={`${category.name} - XION Medical Philippines`}
        description={category.description}
        path={`/products/${categorySlug}`}
      />
      <Header />
      <main className="min-h-screen bg-hud pt-32 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Category Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <div className="inline-block px-4 py-1 bg-[#00FFD1]/10 border border-[#00FFD1]/30 rounded-full mb-4">
              <span className="text-[#00FFD1] text-sm font-mono">{category.category}</span>
            </div>
            <h1 className="text-5xl font-bold text-white mb-6">{category.name}</h1>
            <p className="text-xl text-white/70 max-w-4xl leading-relaxed mb-6">
              {category.description}
            </p>
            {category.overview && (
              <p className="text-white/60 max-w-4xl leading-relaxed">
                {category.overview}
              </p>
            )}
          </motion.div>

          {/* Products Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {category.products.map((product, idx) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="group [perspective:1000px]"
              >
                <TiltCard
                  data-testid={`product-card-${product.slug}`}
                  className="hud-card corner-brackets rounded-lg overflow-hidden h-full flex flex-col"
                >
                  {/* Product Image */}
                  <div className="aspect-video bg-gradient-to-br from-[#00FFD1]/5 to-transparent flex items-center justify-center border-b border-white/10 overflow-hidden">
                    {product.images && product.images[0] && !product.images[0].startsWith('/images/') ? (
                      <img 
                        src={product.images[0]} 
                        alt={product.name}
                        className="w-full h-full object-contain p-4 tilt-lift"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = '<svg class="text-[#00FFD1]/30" width="48" height="48" fill="currentColor"><use href="#package-icon"/></svg>';
                        }}
                      />
                    ) : (
                      <Package size={48} className="text-[#00FFD1]/30 tilt-lift" />
                    )}
                  </div>

                  {/* Product Content */}
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-[#00FFD1] transition-colors">
                      {product.name}
                    </h3>
                    <p className="text-[#00FFD1] text-sm mb-4">{product.tagline}</p>
                    <p className="text-white/60 text-sm mb-6 line-clamp-3">
                      {product.description}
                    </p>

                    {/* Features Preview */}
                    {product.features && product.features.length > 0 && (
                      <div className="mb-6">
                        <ul className="space-y-1">
                          {product.features.slice(0, 3).map((feature, fIdx) => (
                            <li key={fIdx} className="text-white/50 text-xs flex items-start gap-2">
                              <span className="text-[#00FFD1] mt-0.5">•</span>
                              <span>{feature}</span>
                            </li>
                          ))}
                          {product.features.length > 3 && (
                            <li className="text-white/40 text-xs italic">
                              + {product.features.length - 3} more features
                            </li>
                          )}
                        </ul>
                      </div>
                    )}

                    <Link
                      to={`/products/${categorySlug}/${product.slug}`}
                      data-testid={`view-product-${product.slug}`}
                      className="btn-secondary w-full justify-center group-hover:bg-[#00FFD1]/10 mt-auto"
                    >
                      View Details
                      <ArrowRight size={16} />
                    </Link>
                  </div>
                </TiltCard>
              </motion.div>
            ))}
          </div>

          {/* Distributor CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-[#00FFD1]/10 to-transparent border-2 border-[#00FFD1]/50 rounded-lg p-8 text-center mb-12"
          >
            <h2 className="text-2xl font-bold text-white mb-4">
              Available in the Philippines
            </h2>
            <p className="text-white/70 mb-6">
              {productsData.distributor.statement}
            </p>
            <Link to="/contact-ph" className="btn-primary">
              Contact PH Distributor
              <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default CategoryPage;
